import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "../../components/button";
import iosBadge from "../../assets/images/ios-badge.svg";
import androidBadge from "../../assets/images/google-play-badge.svg";
import customersImage from "../../assets/images/onboarding-customers-empty.png";
import usersImage from "../../assets/images/onboarding-users-empty.png";
import sitesImage from "../../assets/images/onboarding-sites-empty.png";

const DashboardEmptyState = ({ needsUsers, needsCustomers, needsSites }) => {
  const navigate = useNavigate();

  return (
    <>
      <div className="p-6 mt-6 flex justify-center items-center">
        <div>
          <p className="text-black dark:text-white text-center text-2xl font-semibold pt-6">
            Welcome to Opspot, we're glad you're here!
          </p>
          <p className="pt-4 text-center text-lg text-gray-700 dark:text-gray-300">
            Let's set things up so you can start saving time.
          </p>
        </div>
      </div>

      <div className="flex flex-row px-6 mt-8 w-5/6 space-x-6 mx-auto">
        <div className="w-1/2 bg-white dark:bg-gray-800 p-6 rounded-lg">
          <div className="relative bg-gray-50 dark:bg-gray-700 rounded-lg w-full h-48 mb-4 flex justify-center items-center overflow-hidden">
            <img src={customersImage} alt="Customers" className="w-full h-full object-cover" />
            {!needsCustomers && (
              <div className="absolute inset-0 bg-black bg-opacity-70 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-16 w-16 text-white" />
              </div>
            )}
          </div>
          <h2 className="mb-2 text-md font-medium dark:text-white">Add your first customers</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Start by adding customer profiles, even if it's your own company.
          </p>
          <div className="pt-4">
            <Button variant="secondary" onClick={() => navigate("/customers/")}>
              Add a customer
            </Button>
          </div>
        </div>

        <div className="w-1/2 bg-white dark:bg-gray-800 p-6 rounded-lg">
          <div className="relative bg-gray-50 dark:bg-gray-700 rounded-lg w-full h-48 mb-4 flex justify-center items-center overflow-hidden">
            <img src={usersImage} alt="Users" className="w-full h-full object-cover" />
            {!needsUsers && (
              <div className="absolute inset-0 bg-black bg-opacity-70 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-16 w-16 text-white" />
              </div>
            )}
          </div>
          <h2 className="mb-2 text-md font-medium dark:text-white">Invite your team</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Add guards so they can start using the mobile app.
          </p>
          <div className="pt-4">
            <Button
              variant="secondary"
              onClick={() => navigate("/settings/users/")}
            >
              Add users
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-row px-6 mt-8 w-5/6 space-x-6 pb-24 mx-auto">
        <div className="w-1/2 bg-white dark:bg-gray-800 p-6 rounded-lg">
          <div className="relative bg-gray-50 dark:bg-gray-700 rounded-lg w-full h-48 mb-4 flex justify-center items-center overflow-hidden">
            <img src={sitesImage} alt="Sites" className="w-full h-full object-cover" />
            {!needsSites && (
              <div className="absolute inset-0 bg-black bg-opacity-70 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-16 w-16 text-white" />
              </div>
            )}
          </div>
          <h2 className="mb-2 text-md font-medium dark:text-white">Add a site</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Set up a site where your guards will check in and report activity.
          </p>
          <div className="pt-4">
            <Button variant="secondary" onClick={() => navigate("/sites/")}>
              Add a site
            </Button>
          </div>
        </div>

        <div className="w-1/2 px-6 pt-6 pb-8 flex">
          <div className="bg-black rounded-lg flex flex-col justify-center items-center text-white text-center p-6 w-full h-full">
            <h2 className="mb-2 text-md font-medium">Get the Opspot app</h2>
            <p className="text-gray-200 text-sm">
              The Opspot mobile app is your team's tool for capturing data in the field
            </p>
            <div className="mt-8 flex">
              <div className="w-1/2 px-6">
                <a
                  href="https://apps.apple.com/ca/app/opspot/id6587551457"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={iosBadge} alt="Download on the App Store" />
                </a>
              </div>
              <div className="w-1/2 px-6">
                <a
                  href="https://play.google.com/store/apps/details?id=com.anonymous.opspot&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={androidBadge} alt="Get it on Google Play" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardEmptyState;
